import {
  ArrowLeftOutlined,
  CheckCircleFilled,
  CheckOutlined,
  DownOutlined,
  InfoCircleOutlined,
  LoadingOutlined,
  ThunderboltOutlined,
  ZoomInOutlined,
} from '@ant-design/icons'
import { Alert, Button, Image, Input, Skeleton, Tag, Tooltip, message } from 'antd'
import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { useNavigate, useParams } from 'react-router-dom'
import {
  getVirtualIpCharacterSelectionWorkspace,
  getVirtualIpDraft,
  getVirtualIpPositioningProfile,
  regenerateVirtualIpCharacterSelectionBatch,
    retryVirtualIpCharacterSelectionRefinement,
  selectVirtualIpCharacterSelectionFinal,
  submitVirtualIpCharacterSelectionRefinement,
  type SaasApiError,
  type VirtualIpCharacterCandidateKind,
  type VirtualIpCharacterRefinementRequestOut,
  type VirtualIpCharacterSelectionBatch,
  type VirtualIpCharacterSelectionCandidate,
  type VirtualIpCharacterSelectionRegenerateMode,
  type VirtualIpCharacterSelectionWorkspace,
    type VirtualIpDraft,
  type VirtualIpPositioningProfile,
} from '../../api'
import { useModalScrollLock } from '../../utils/scrollLock'
import { formatCandidatePlanLabel, formatCharacterBatchTitle, sortCharacterBatchesNewestFirst } from './virtualIpCharacterHistory'
import { virtualIpCompletedThrough } from './virtualIpProgress'
import './virtualIp.css'

export type VirtualIpCharacterWorkbenchProps = {
  /** When true, hides page chrome — parent Step5 owns top bar / stepper. */
  embedded?: boolean
  /** Called after workspace mutations so parent can refresh progress. */
  onFactsChange?: () => void
}

/** VISTOERA_VIRTUAL_IP_STEP6_CHARACTER_SELECTION_AND_REFINEMENT_V1 —
    * "比较 → {t('common.select')} → 微调 → 最终确认".
 *
 * RECONCILIATION (see `app.saas.models.virtual_ip_character_selection`
 * module docstring for the full history) — every candidate rendered
 * here comes straight from the REAL STEP 5 pipeline
 * (`GET .../character-selection/workspace` →
 * `virtual_ip_character_selection_service.get_workspace_state`, itself
 * built on STEP 5's own `list_batches_for_draft`/`batch_out`), never a
 * hand-seeded/hardcoded candidate count. This page never dispatches a
 * STEP 5 batch itself (that is STEP 5's own "{t('step5.generateCharacterPlans')}" button on
    * its own page) — if no batch exists yet at all, this page's only job
 * is to point the user back to STEP 5, never to silently trigger a
 * generation STEP 5's own UI/consent flow didn't ask for.
 *
 * PROVENANCE (spec §2/§32) — Provider/Run ID/Task ID/生成模型/生成时间
 * stay backend-retained-only (Platform Admin/audit/forensic/cost); this
 * page never reads `VirtualIpCharacterRun`'s own fields at all.
 * TENANT_UI_DISPLAYED = NO, DATA_RETAINED = YES. */

const POLL_INTERVAL_MS = 4000
const IN_FLIGHT_BATCH_STATUSES = new Set(['DRAFT', 'READY_TO_GENERATE', 'QUEUED', 'RUNNING', 'WAITING_PROVIDER', 'RESULT_RECEIVED'])

type PageState = 'loading' | 'error' | 'needs_step4' | 'needs_step5' | 'ready'

/** A normalized view over the two real underlying shapes this page ever
   * selects/refines/previews — a real STEP 5 candidate
 * (`candidate_kind: 'original'`) or one of this feature's own refinement
 * versions (`candidate_kind: 'refinement'`) — so the Candidate Card /
 * Lineage Strip / Regenerate "参考方案" picker components never need to
 * know which underlying table a given item came from. */
interface SelectableItem {
  id: string
  kind: VirtualIpCharacterCandidateKind
  conceptName: string | null
  shortDifference: string | null
  visualTags: string[]
    thumbnailUrl: string | null
  fullUrl: string | null
  slotLabel: string | null
  versionIndex: number
  lineageRootId: string
}

function candidateToItem(c: VirtualIpCharacterSelectionCandidate): SelectableItem {
  return {
    id: c.candidate_id,
    kind: 'original',
    conceptName: c.concept_name,
    shortDifference: c.short_difference,
    visualTags: c.visual_tags,
    thumbnailUrl: c.thumbnail_url,
    fullUrl: c.download_url,
    slotLabel: c.slot_label,
    versionIndex: 0,
    lineageRootId: c.candidate_id,
  }
    }

function versionToItem(v: VirtualIpCharacterSelectionWorkspace['refinement_versions'][number]): SelectableItem {
  return {
    id: v.version_id,
    kind: 'refinement',
    conceptName: v.concept_name,
    shortDifference: v.short_difference,
    visualTags: v.visual_tags,
    thumbnailUrl: v.canonical_asset_reference.thumbnail_url,
   fullUrl: v.canonical_asset_reference.full_url,
    slotLabel: null,
    versionIndex: v.version_index,
    lineageRootId: v.lineage_root_id,
  }
}

function isWorkspaceBusy(data: VirtualIpCharacterSelectionWorkspace | null): boolean {
  if (!data) return false
  if (data.batches.some((b) => IN_FLIGHT_BATCH_STATUSES.has(b.status))) return true
   if (data.refinement_requests.some((r) => r.status === 'pending')) return true
  return false
}

function asText(value: unknown): string {
  return typeof value === 'string' && value.trim() ? value.trim() : ''
}

function asStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string' && v.length > 0) : []
   }

function appendChip(current: string, chip: string): string {
  if (current.includes(chip)) return current
  const trimmed = current.trim()
  return trimmed ? `${trimmed}，${chip}` : chip
}

function itemDisplayLabel(item: SelectableItem, t: TFunction): string {
  if (item.kind === 'original') return `${item.slotLabel ?? '?'} · ${item.conceptName || t('common.unnamedPlan')}`
  return t('characterWorkbench.refinementLineageLabel', { version: item.versionIndex, name: item.conceptName || t('common.unnamedPlan') })
}

export default function VirtualIpCharacterWorkbench({ embedded = false, onFactsChange }: VirtualIpCharacterWorkbenchProps) {
  const { t } = useTranslation(['virtualIp', 'actions', 'status', 'validation'])
  const { workspaceId = '', projectId = '' } = useParams()
  const navigate = useNavigate()

  const [draft, setDraft] = useState<VirtualIpDraft | null>(null)
  const [profile, setProfile] = useState<VirtualIpPositioningProfile | null>(null)
  const [workspace, setWorkspace] = useState<VirtualIpCharacterSelectionWorkspace | null>(null)
  const [pageState, setPageState] = useState<PageState>('loading')
  const [loadError, setLoadError] = useState<string | null>(null)
  const [selectingId, setSelectingId] = useState<string | null>(null)
  const [regenerating, setRegenerating] = useState(false)
  const [refinementText, setRefinementText] = useState('')
  const [refining, setRefining] = useState(false)
  const [retryingId, setRetryingId] = useState<string | null>(null)
  const [previewSrc, setPreviewSrc] = useState<string | null>(null)

  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
   const fetchWorkspace = useCallback(
    (draftId: string) => {
      return getVirtualIpCharacterSelectionWorkspace(workspaceId, draftId).then((r) => {
        setWorkspace(r.data)
        return r.data
      })
    },
    [workspaceId],
  )
  const schedulePoll = useCallback(
   (draftId: string) => {
      if (pollTimer.current) clearTimeout(pollTimer.current)
      pollTimer.current = setTimeout(() => {
        fetchWorkspace(draftId)
          .then((data) => {
            if (isWorkspaceBusy(data)) schedulePoll(draftId)
          })
          .catch(() => schedulePoll(draftId))
      }, POLL_INTERVAL_MS)
    },
   [fetchWorkspace],
  )

  const load = useCallback(() => {
    if (!workspaceId) return
    let cancelled = false
    setPageState('loading')
    setLoadError(null)
    getVirtualIpDraft(workspaceId, projectId)
      .then((dRes) => {
        if (cancelled) return
   setDraft(dRes.data)
        if (virtualIpCompletedThrough(dRes.data.status) < 4) {
          setPageState('needs_step4')
          return
        }
        // Best-effort sidebar data — its own failure must never block the
        // canonical character workspace from loading.
        getVirtualIpPositioningProfile(workspaceId, dRes.data.id)
          .then((r) => !cancelled && setProfile(r.data))
          .catch(() => undefined)
           return fetchWorkspace(dRes.data.id).then((data) => {
          if (cancelled) return
          if (!data.gate_ok) {
            setPageState('needs_step4')
            return
          }
          if (!data.has_any_batch) {
            setPageState('needs_step5')
            return
   }
          setPageState('ready')
          if (isWorkspaceBusy(data)) schedulePoll(dRes.data.id)
          return undefined
        })
      })
      .catch((e) => {
        if (cancelled) return
        setLoadError((e as SaasApiError).message || t('common.loadFailedRefresh'))
        setPageState('error')
   })
    return () => {
      cancelled = true
    }
  }, [workspaceId, fetchWorkspace, schedulePoll])

  useEffect(() => {
    const cancel = load()
    return () => {
      cancel?.()
      if (pollTimer.current) clearTimeout(pollTimer.current)
   }
  }, [load])

  useModalScrollLock(!!previewSrc)

  const allOriginalCandidates = useMemo<VirtualIpCharacterSelectionCandidate[]>(
    () => workspace?.batches.flatMap((b) => b.candidates) ?? [],
    [workspace],
  )
  const allItems = useMemo<SelectableItem[]>(() => {
   const originals = allOriginalCandidates.map(candidateToItem)
    const versions = (workspace?.refinement_versions ?? []).map(versionToItem)
    return [...originals, ...versions]
  }, [allOriginalCandidates, workspace])

  const initialTotal = allOriginalCandidates.length
  const hasPendingBatch = workspace?.batches.some((b) => IN_FLIGHT_BATCH_STATUSES.has(b.status)) ?? false
  const finalSelectedId = workspace?.selection?.selected_id ?? null
  const finalSelectedKind = workspace?.selection?.selected_kind ?? null
  const selectedId = finalSelectedId
  const selectedKind = finalSelectedKind
  const sortedBatches = useMemo(
    () => sortCharacterBatchesNewestFirst(workspace?.batches ?? []),
    [workspace?.batches],
  )
  const selectedItem = useMemo(
   () => (selectedId ? allItems.find((i) => i.id === selectedId && i.kind === selectedKind) ?? null : null),
    [allItems, selectedId, selectedKind],
  )
  const finalSelectedItem = useMemo(
    () =>
      finalSelectedId
        ? allItems.find((i) => i.id === finalSelectedId && i.kind === finalSelectedKind) ?? null
        : null,
    [allItems, finalSelectedId, finalSelectedKind],
  )
  const lineageRootId = selectedItem?.lineageRootId ?? null
  const lineageVersions = useMemo(() => {
    if (!lineageRootId) return []
    return allItems.filter((i) => i.lineageRootId === lineageRootId).sort((a, b) => a.versionIndex - b.versionIndex)
  }, [allItems, lineageRootId])
  const latestRefinement: VirtualIpCharacterRefinementRequestOut | null = workspace?.refinement_requests.length
   ? workspace.refinement_requests[workspace.refinement_requests.length - 1]
    : null

  const goStep4 = () => navigate(`/workspace/${workspaceId}/virtual-ip/projects/${projectId}/step-4`)
  const goStep6Voice = () => navigate(`/workspace/${workspaceId}/virtual-ip/projects/${projectId}/step-6`)

  const bumpFacts = () => onFactsChange?.()

  const handleRegenerateSubmit = (payload: {
    mode: VirtualIpCharacterSelectionRegenerateMode
    reference_candidate_id?: string | null
   reference_candidate_kind?: VirtualIpCharacterCandidateKind
    instruction?: string | null
  }) => {
    if (!draft) return
    setRegenerating(true)
    regenerateVirtualIpCharacterSelectionBatch(workspaceId, draft.id, payload)
      .then((r) => {
        setWorkspace(r.data)
        setRegenerating(false)
        message.success(t('characterWorkbench.dispatchSuccess'))
        bumpFacts()
        schedulePoll(draft.id)
      })
      .catch((e) => {
        setRegenerating(false)
        message.error((e as SaasApiError).message || t('common.submitFailed'))
      })
  }
  const handleSelect = (id: string, kind: VirtualIpCharacterCandidateKind) => {
    if (!draft) return
    if (finalSelectedId === id && finalSelectedKind === kind) return
    setSelectingId(id)
    selectVirtualIpCharacterSelectionFinal(workspaceId, draft.id, { selected_id: id, selected_kind: kind })
      .then((r) => {
        setWorkspace(r.data)
        setSelectingId(null)
        bumpFacts()
      })
      .catch((e) => {
        setSelectingId(null)
        message.error((e as SaasApiError).message || t('common.selectFailed'))
      })
  }

  const handleRegenerate = () => {
    handleRegenerateSubmit({ mode: 'keep_positioning' })
  }

  const handleRefinementSubmit = () => {
    if (!draft || !selectedId || !selectedKind || !refinementText.trim()) return
    setRefining(true)
    submitVirtualIpCharacterSelectionRefinement(workspaceId, draft.id, {
      base_candidate_id: selectedId,
      base_candidate_kind: selectedKind,
      instruction: refinementText.trim(),
    })
   .then((r) => {
        setWorkspace(r.data)
        setRefining(false)
        setRefinementText('')
        message.success(t('characterWorkbench.refineDispatchSuccess'))
        bumpFacts()
        schedulePoll(draft.id)
      })
      .catch((e) => {
        setRefining(false)
        message.error((e as SaasApiError).message || t('common.submitFailed'))
   })
  }

  const handleRetry = (requestId: string) => {
    if (!draft) return
    setRetryingId(requestId)
    retryVirtualIpCharacterSelectionRefinement(workspaceId, draft.id, requestId)
      .then((r) => {
        setWorkspace(r.data)
        setRetryingId(null)
   schedulePoll(draft.id)
      })
      .catch((e) => {
        setRetryingId(null)
        message.error((e as SaasApiError).message || t('step5.retryFailed'))
      })
  }

  if (embedded && pageState === 'needs_step5') return null

  return (
   <div className={`tc-vip-char-workbench${embedded ? ' is-embedded' : ''}`} data-testid="virtual-ip-character-workbench">
      {!embedded && (
        <TopBar
          onBack={() => navigate(`/workspace/${workspaceId}/virtual-ip/projects/${projectId}/step-5`)}
          canGoNext={Boolean(finalSelectedId)}
          onNext={goStep6Voice}
        />
      )}

      {pageState === 'loading' && (
        <>
          <Skeleton active paragraph={{ rows: 2 }} />
          <Skeleton active paragraph={{ rows: 8 }} style={{ marginTop: 20 }} />
        </>
   )}
      {pageState === 'error' && (
        <Alert
          type="error"
          showIcon
          message={t('common.loadFailed')} description={loadError}
          action={
            <Button size="small" onClick={load}>
              {t('actions:retry')}
   </Button>
          }
        />
      )}

      {pageState === 'needs_step4' && (
        <Alert
          type="info"
          showIcon
          message={t('characterWorkbench.prerequisiteMessage')} description={workspace?.gate_reason || t('characterWorkbench.prerequisiteDefault')}
   action={
            <Button type="primary" size="small" onClick={goStep4}>
              {t('common.backToStep', { step: 4 })}
            </Button>
          }
          data-testid="vip-step6-needs-step4"
        />
      )}

      {pageState === 'needs_step5' && !embedded && (
        <Alert
   type="info"
          showIcon
          icon={<InfoCircleOutlined />}
          message={t('characterWorkbench.noBatches')} description={t('characterWorkbench.noBatchesDesc')}
          data-testid="vip-char-needs-generation"
        />
      )}

      {pageState === 'ready' && workspace && (
        <>
          {workspace.real_person_mode && (
            <Alert
              type="info"
              showIcon
              message={t('characterWorkbench.realPersonBanner')} description={t('characterWorkbench.realPersonBannerDesc')} style={{ marginBottom: 4 }}
              data-testid="vip-char-real-person-tip"
            />
          )}

          {initialTotal === 0 ? (
            <div className="tc-vip-char-banner is-pending" data-testid="vip-char-banner-pending">
              <LoadingOutlined className="tc-vip-char-banner-icon" />
              <div>
                <div className="tc-vip-char-banner-title">{t('characterWorkbench.generatingBanner')}</div>
                <p className="tc-vip-char-banner-desc">{t('characterWorkbench.generatingBannerDesc')}</p>
              </div>
            </div>
          ) : (
            <BatchHistoryToolbar
              hasBatches={sortedBatches.length > 0}
              hasPendingBatch={hasPendingBatch}
              regenerating={regenerating}
              onRegenerate={handleRegenerate}
            />
          )}

          {embedded ? (
            <div className="tc-vip-char-embedded-main">
              <CandidateComparisonSection
                batches={sortedBatches}
                selectedId={selectedId}
                selectedKind={selectedKind}
                selectingId={selectingId}
                onSelect={handleSelect}
                onPreview={setPreviewSrc}
              />
              {finalSelectedItem && <CurrentSelectionSummary item={finalSelectedItem} />}
              <RefinementWorkspace
                selectedItem={selectedItem}
                lineageVersions={lineageVersions}
                selectedId={selectedId}
                selectedKind={selectedKind}
                selectingId={selectingId}
                onSelectVersion={handleSelect}
                onPreview={setPreviewSrc}
                refinementText={refinementText}
                setRefinementText={setRefinementText}
                onSubmitRefinement={handleRefinementSubmit}
                refining={refining}
                latestRefinement={latestRefinement}
                onRetry={handleRetry}
                retryingId={retryingId}
                realPersonMode={workspace.real_person_mode}
              />
            </div>
          ) : (
            <div className="tc-vip-layout">
              <div className="tc-vip-main">
                <CandidateComparisonSection
                  batches={sortedBatches}
                  selectedId={selectedId}
                  selectedKind={selectedKind}
                  selectingId={selectingId}
                  onSelect={handleSelect}
                  onPreview={setPreviewSrc}
                />
                {finalSelectedItem && <CurrentSelectionSummary item={finalSelectedItem} />}
                <RefinementWorkspace
                  selectedItem={selectedItem}
                  lineageVersions={lineageVersions}
                  selectedId={selectedId}
                  selectedKind={selectedKind}
                  selectingId={selectingId}
                  onSelectVersion={handleSelect}
                  onPreview={setPreviewSrc}
                  refinementText={refinementText}
                  setRefinementText={setRefinementText}
                  onSubmitRefinement={handleRefinementSubmit}
                  refining={refining}
                  latestRefinement={latestRefinement}
                  onRetry={handleRetry}
                  retryingId={retryingId}
                  realPersonMode={workspace.real_person_mode}
                />
              </div>
              <Sidebar profile={profile} onViewFullProfile={goStep4} />
            </div>
          )}
        </>
      )}

      {previewSrc && (
   <Image
          src={previewSrc}
          style={{ display: 'none' }}
          preview={{
            visible: true,
            src: previewSrc,
            onVisibleChange: (visible) => {
              if (!visible) setPreviewSrc(null)
            },
          }}
   />
      )}
    </div>
  )
}

function CurrentSelectionSummary({ item }: { item: SelectableItem }) {
  const { t } = useTranslation(['virtualIp', 'actions', 'status', 'validation'])
  return (
    <div className="tc-data-card tc-vip-card tc-vip-char-current-selection is-compact" data-testid="vip-char-current-selection">
      <div className="tc-vip-char-current-selection-body">
        <span className="tc-vip-char-current-selection-label">{t('characterWorkbench.currentSelection')}</span>
        {item.thumbnailUrl ? (
          <img src={item.thumbnailUrl} alt="" className="tc-vip-char-current-selection-thumb" />
        ) : null}
        <span className="tc-vip-char-current-selection-name">{itemDisplayLabel(item, t)}</span>
      </div>
    </div>
  )
}

/** Top action bar (spec §1) — selections persist on confirm only.
 * selection/refinement below is already persisted the instant the user
 * acts on it (`PUT .../selection`, `POST .../refinements`), so there is
 * no separate draft-only state for this screen to save (same rationale
 * as STEP 4/5's own disabled "{t('common.saveAsDraft')}" states). */
   function TopBar({ onBack, canGoNext, onNext }: { onBack: () => void; canGoNext: boolean; onNext: () => void }) {
  const { t } = useTranslation(['virtualIp', 'actions', 'status', 'validation'])
  return (
    <div className="tc-vip-step4-topbar">
      <div>
        <Button
          className="tc-vip-step4-topbar-back"
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={onBack}
          data-testid="vip-step6-prev-button"
   >
          {t('common.backToPrevious')}
        </Button>
        <div className="tc-page-header tc-vip-step4-topbar-titles" style={{ marginBottom: 0 }}>
          <div>
            <h1 className="tc-page-title">{t('common.pageTitle')}</h1>
            <div className="tc-vip-step-label">
              <span>STEP 5 / 7</span>
              <span className="tc-vip-step-title">{t('characterWorkbench.title')}</span>
            </div>
   <p className="tc-page-desc">{t('step5.description')}</p>
          </div>
        </div>
      </div>
      <div className="tc-vip-step4-topbar-actions">
        <Tooltip title={t('characterWorkbench.saveDraftTooltip')}>
          <span>
            <Button disabled data-testid="vip-step6-save-draft-button">
              {t('common.saveAsDraft')}
   </Button>
          </span>
        </Tooltip>
        {canGoNext ? (
          <Button type="primary" onClick={onNext} data-testid="vip-char-next-voice-button">
            {t('step5.nextVoiceArrow')}
          </Button>
        ) : (
          <Tooltip title={t('step5.selectCharacterFirst')}>
            <span>
   <Button type="primary" disabled data-testid="vip-char-next-voice-button">
                {t('step5.nextVoiceArrow')}
              </Button>
            </span>
          </Tooltip>
        )}
      </div>
    </div>
  )
}

function BatchHistoryToolbar({
  hasBatches,
  hasPendingBatch,
  regenerating,
  onRegenerate,
}: {
  hasBatches: boolean
  hasPendingBatch: boolean
  regenerating: boolean
  onRegenerate: () => void
}) {
  const { t } = useTranslation(['virtualIp', 'actions', 'status', 'validation'])
  const label = hasBatches ? t('characterWorkbench.regenerateFour') : t('characterWorkbench.generateFour')
  const busy = regenerating || hasPendingBatch
  return (
    <div className="tc-vip-char-batch-toolbar" data-testid="vip-char-batch-toolbar">
      <p className="tc-vip-char-batch-toolbar-hint">{t('characterWorkbench.batchToolbarHint')}</p>
      <Tooltip title={hasPendingBatch ? t('characterWorkbench.batchPending') : ''}>
        <span>
          <Button
            type="primary"
            icon={<ThunderboltOutlined />}
            disabled={busy}
            loading={regenerating}
            onClick={onRegenerate}
            data-testid="vip-char-regenerate-button"
          >
            {busy && !regenerating ? t('step5.generatingEllipsis') : label}
          </Button>
        </span>
      </Tooltip>
    </div>
  )
}

/** Candidate Comparison (spec §5-§10) — renders every real STEP 5 batch's
 * own candidates, grouped into "第 N 轮方案" only once a second batch
   * actually exists (spec §9's own worked example), so a single-batch
 * workspace (the common case) stays a flat grid. Refinement versions
 * never render here (spec §21/§22 — they live exclusively in
 * `RefinementWorkspace`'s lineage strip, never duplicated in this grid). */
function CandidateComparisonSection({
  batches,
  selectedId,
  selectedKind,
  selectingId,
  onSelect,
  onPreview,
}: {
  batches: VirtualIpCharacterSelectionBatch[]
  selectedId: string | null
  selectedKind: VirtualIpCharacterCandidateKind | null
  selectingId: string | null
  onSelect: (id: string, kind: VirtualIpCharacterCandidateKind) => void
  onPreview: (url: string) => void
}) {
  const { t } = useTranslation(['virtualIp', 'actions', 'status', 'validation'])
  return (
    <div className="tc-data-card tc-vip-card tc-vip-char-comparison" data-testid="vip-char-comparison">
      <div className="tc-vip-card-title">{t('characterWorkbench.generatedResults')}</div>
      {batches.map((batch, batchIdx) => {
        const candidates = batch.candidates.slice().sort((a, b) => a.sort_order - b.sort_order)
        const isPending = IN_FLIGHT_BATCH_STATUSES.has(batch.status)
        const isFailed = batch.status === 'FAILED'
        const isLatest = batchIdx === 0
        const title = formatCharacterBatchTitle(t, batch.round_index, isLatest)
        const createdLabel = formatBatchCreatedAt(batch.created_at)
        return (
          <div key={batch.generation_batch_id} className="tc-vip-char-round" data-testid="vip-char-round">
            <div className="tc-vip-char-round-head">
              <div className="tc-vip-char-round-label">
                {title}
                {isLatest && (
                  <Tag className="tc-vip-char-round-new" color="purple">
                    NEW
                  </Tag>
                )}
              </div>
              {createdLabel && <span className="tc-vip-char-round-time">{createdLabel}</span>}
            </div>
            {isPending && (
              <div className="tc-vip-char-round-pending" data-testid="vip-char-round-pending">
                <LoadingOutlined /> {t('characterWorkbench.generatingNewPlans')}
              </div>
            )}
            {isFailed && (
              <Alert
                type="error"
                showIcon
                message={t('characterWorkbench.batchFailed')} description={batch.error_message || t('characterWorkbench.batchFailedDefault')}
                style={{ marginBottom: 12 }}
                data-testid="vip-char-round-failed"
              />
            )}
            {candidates.length > 0 && (
              <div className="tc-vip-char-grid">
                {candidates.map((c, candidateIdx) => (
                  <CandidateCard
                    key={c.candidate_id}
                    candidate={c}
                    planLabel={formatCandidatePlanLabel(t, batch.round_index, candidateIdx + 1)}
                    isSelected={c.candidate_id === selectedId && selectedKind === 'original'}
                    isBusy={selectingId === c.candidate_id}
                    onSelect={() => onSelect(c.candidate_id, 'original')}
                    onPreview={onPreview}
                  />
                ))}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

function formatBatchCreatedAt(createdAt: string | null | undefined): string | null {
  if (!createdAt) return null
  const parsed = Date.parse(createdAt)
  if (Number.isNaN(parsed)) return null
  const d = new Date(parsed)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}
function CandidateCard({
  candidate,
  planLabel,
  isSelected,
  isBusy,
  onSelect,
  onPreview,
}: {
  candidate: VirtualIpCharacterSelectionCandidate
  planLabel: string
  isSelected: boolean
  isBusy: boolean
  onSelect: () => void
  onPreview: (url: string) => void
}) {
  const { t } = useTranslation(['virtualIp', 'actions', 'status', 'validation'])
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      if (!isBusy) onSelect()
    }
  }

  return (
    <div
      className={`tc-vip-char-card${isSelected ? ' is-selected' : ''}${isBusy ? ' is-busy' : ''}`}
      data-testid="vip-char-card"
      role="button"
      tabIndex={0}
      aria-selected={isSelected}
      aria-busy={isBusy}
      onClick={() => {
        if (!isBusy) onSelect()
      }}
      onKeyDown={handleKeyDown}
    >
      <div className={`tc-vip-char-card-radio${isSelected ? ' is-selected' : ''}`} aria-hidden>
        {isSelected ? <CheckOutlined /> : null}
      </div>
      <div className="tc-vip-char-card-image-wrap">
        {candidate.thumbnail_url ? (
          <img src={candidate.thumbnail_url} alt={planLabel} loading="lazy" />
        ) : (
          <span className="tc-vip-char-card-image-empty">{t('characterWorkbench.noImage')}</span>
        )}
        {candidate.download_url && (
          <button
            type="button"
            className="tc-vip-char-card-zoom"
            aria-label={t('characterWorkbench.viewLarge')} onClick={(event) => {
              event.stopPropagation()
              onPreview(candidate.download_url!)
            }}
          >
            <ZoomInOutlined />
          </button>
        )}
      </div>
      <div className="tc-vip-char-card-body">
        <div className="tc-vip-char-card-name">{planLabel}</div>
        {candidate.concept_name && candidate.concept_name !== planLabel && (
          <div className="tc-vip-char-card-subname">{candidate.concept_name}</div>
        )}
        {candidate.visual_tags.length > 0 && (
          <div className="tc-vip-char-card-tags">
            {candidate.visual_tags.slice(0, 3).map((tag, idx) => (
              <Tag key={idx} className="tc-vip-analysis-tag">
                {tag}
              </Tag>
            ))}
          </div>
        )}
      </div>
      {isBusy && (
        <div className="tc-vip-char-card-busy" aria-hidden>
          <LoadingOutlined />
        </div>
      )}
    </div>
  )
}

const QUICK_CHIPS = ['characterWorkbench.quickChips.0', 'characterWorkbench.quickChips.1', 'characterWorkbench.quickChips.2', 'characterWorkbench.quickChips.3', 'characterWorkbench.quickChips.4'] as const
const MORE_CHIPS = ['characterWorkbench.moreChips.0', 'characterWorkbench.moreChips.1', 'characterWorkbench.moreChips.2', 'characterWorkbench.moreChips.3'] as const

/** spec §13 — clicking a chip only appends it into the free-text
 * instruction (never triggers generation directly); the same chip is
   * never appended twice. */
function QuickChips({ text, onAppend }: { text: string; onAppend: (chip: string) => void }) {
  const { t } = useTranslation(['virtualIp', 'actions', 'status', 'validation'])
  const [showMore, setShowMore] = useState(false)
  const quickChips = QUICK_CHIPS.map((key) => t(key))
  const moreChips = MORE_CHIPS.map((key) => t(key))
  return (
    <div className="tc-vip-quick-chips" data-testid="vip-char-quick-chips">
      {quickChips.map((chip) => (
        <span key={chip} className={`tc-vip-chip${text.includes(chip) ? ' is-selected' : ''}`} onClick={() => onAppend(chip)}>
          {chip}
        </span>
      ))}
   {showMore &&
        moreChips.map((chip) => (
          <span key={chip} className={`tc-vip-chip${text.includes(chip) ? ' is-selected' : ''}`} onClick={() => onAppend(chip)}>
            {chip}
          </span>
        ))}
      <span className="tc-vip-chip" onClick={() => setShowMore((s) => !s)}>
        {showMore ? t('common.collapse') : t('characterWorkbench.moreSuggestions')} <DownOutlined />
      </span>
   </div>
  )
}

/** spec §21/§22 lineage strip — "原始方案 / 优化版本 1 / 优化版本 2…" as
 * horizontal thumbnails, no technical candidate/version IDs shown.
 * Clicking a thumbnail's own "{t('common.select')}" re-targets the ACTIVE base character
 * to that version (spec §7 — still only ONE active selection at any
 * time). */
function LineageStrip({
  versions,
   selectedId,
  selectedKind,
  selectingId,
  onSelect,
  onPreview,
}: {
  versions: SelectableItem[]
  selectedId: string | null
  selectedKind: VirtualIpCharacterCandidateKind | null
  selectingId: string | null
   onSelect: (id: string, kind: VirtualIpCharacterCandidateKind) => void
  onPreview: (url: string) => void
}) {
  const { t } = useTranslation(['virtualIp', 'actions', 'status', 'validation'])
  if (versions.length <= 1) return null
  return (
    <div className="tc-vip-lineage-strip" data-testid="vip-char-lineage-strip">
      {versions.map((v) => {
        const isSelected = v.id === selectedId && v.kind === selectedKind
        return (
   <div key={`${v.kind}-${v.id}`} className={`tc-vip-lineage-thumb${isSelected ? ' is-selected' : ''}`}>
            <button
              type="button"
              className="tc-vip-lineage-thumb-img"
              onClick={() => v.fullUrl && onPreview(v.fullUrl)}
              aria-label={t('characterWorkbench.viewLarge')}
            >
              {v.thumbnailUrl && <img src={v.thumbnailUrl} alt={v.conceptName ?? ''} loading="lazy" />}
            </button>
   <div className="tc-vip-lineage-thumb-label">{v.versionIndex === 0 ? t('characterWorkbench.originalPlan') : t('characterWorkbench.refinementVersion', { version: v.versionIndex })}</div>
            {isSelected ? (
              <span className="tc-vip-lineage-thumb-selected">
                <CheckCircleFilled /> {t('common.selected')}
              </span>
            ) : (
              <Button size="small" type="link" loading={selectingId === v.id} onClick={() => onSelect(v.id, v.kind)}>
                {t('common.select')}
   </Button>
            )}
          </div>
        )
      })}
    </div>
  )
}

/** spec §12-§22 refinement workspace — hidden until a base character is
 * actually selected (the whole section operates on "所选形象"), and
   * always keeps the base candidate's identity fixed as `base_candidate_id`
 * (spec §15/§19) — this component itself never lets the user edit which
 * image is the base beyond picking a different lineage version above. */
function RefinementWorkspace({
  selectedItem,
  lineageVersions,
  selectedId,
  selectedKind,
  selectingId,
  onSelectVersion,
   onPreview,
  refinementText,
  setRefinementText,
  onSubmitRefinement,
  refining,
  latestRefinement,
  onRetry,
  retryingId,
  realPersonMode,
}: {
   selectedItem: SelectableItem | null
  lineageVersions: SelectableItem[]
  selectedId: string | null
  selectedKind: VirtualIpCharacterCandidateKind | null
  selectingId: string | null
  onSelectVersion: (id: string, kind: VirtualIpCharacterCandidateKind) => void
  onPreview: (url: string) => void
  refinementText: string
  setRefinementText: (v: string) => void
  onSubmitRefinement: () => void
   refining: boolean
  latestRefinement: VirtualIpCharacterRefinementRequestOut | null
  onRetry: (requestId: string) => void
  retryingId: string | null
  realPersonMode: boolean
}) {
  const { t } = useTranslation(['virtualIp', 'actions', 'status', 'validation'])
  if (!selectedItem) {
    return (
      <div className="tc-data-card tc-vip-card" data-testid="vip-char-refinement-empty">
        <div className="tc-vip-card-title">{t('characterWorkbench.refineTitle')}</div>
   <p className="tc-vip-card-hint">{t('characterWorkbench.refineSelectFirst')}</p>
      </div>
    )
  }

  const isRefining = latestRefinement?.status === 'pending'
  const isFailed = latestRefinement?.status === 'failed'

  return (
    <div className="tc-data-card tc-vip-card" data-testid="vip-char-refinement">
   <div className="tc-vip-card-title">{t('characterWorkbench.refineTitle')}</div>
      <p className="tc-vip-card-desc">{t('characterWorkbench.refineDesc')}</p>

      {realPersonMode && (
        <Alert type="info" showIcon message={t('characterWorkbench.realPersonRefineHint')} style={{ margin: '2px 0' }} />
      )}

      <LineageStrip
        versions={lineageVersions}
   selectedId={selectedId}
        selectedKind={selectedKind}
        selectingId={selectingId}
        onSelect={onSelectVersion}
        onPreview={onPreview}
      />

      {isRefining && (
        <Alert
          type="info"
   showIcon
          icon={<LoadingOutlined />}
          message={t('characterWorkbench.refining')} description={t('characterWorkbench.refiningDesc')}
          data-testid="vip-char-refinement-pending"
        />
      )}

      {isFailed && latestRefinement && (
        <Alert
   type="error"
          showIcon
          message={t('characterWorkbench.refineFailed')} description={latestRefinement.error_message || t('characterWorkbench.refineFailedDefault')}
          data-testid="vip-char-refinement-failed"
          action={
            <Button size="small" loading={retryingId === latestRefinement.request_id} onClick={() => onRetry(latestRefinement.request_id)}>
              {t('common.retryAttempt')}
   </Button>
          }
        />
      )}

      <Input.TextArea
        rows={3}
        maxLength={500}
        showCount
        value={refinementText}
   onChange={(e) => setRefinementText(e.target.value)}
        placeholder={t('characterWorkbench.refinePlaceholder')}
        disabled={isRefining}
        data-testid="vip-char-refinement-textarea"
      />

      <QuickChips text={refinementText} onAppend={(chip) => setRefinementText(appendChip(refinementText, chip))} />
      <Button
        type="primary"
   icon={<ThunderboltOutlined />}
        disabled={!refinementText.trim() || isRefining}
        loading={refining}
        onClick={onSubmitRefinement}
        style={{ alignSelf: 'flex-start' }}
        data-testid="vip-char-refinement-submit"
      >
        {t('characterWorkbench.refineSelected')}
      </Button>
   </div>
  )
}

function QuickLine({ label, value }: { label: string; value: string }) {
  return (
    <div className="tc-vip-char-quick-line">
      <span className="tc-vip-char-quick-line-label">{label}</span>
      <span className="tc-vip-char-quick-line-value">{value}</span>
    </div>
  )
   }

/** Right sidebar (spec §31/§32) — ONLY {t('step5.positioningSummary')} / 形象优化{t('step5.tipsTitle')} /
 * {t('step5.helpTitle')}. Deliberately no "本次生成信息" block: Provider/Run
 * ID/Execution ID/生成模型/生成时间 stay backend-only (audit/debug/cost —
 * spec §2/§32), never rendered here. */
function Sidebar({ profile, onViewFullProfile }: { profile: VirtualIpPositioningProfile | null; onViewFullProfile: () => void }) {
  const { t } = useTranslation(['virtualIp', 'actions', 'status', 'validation'])
  const p = (profile?.confirmed_profile ?? profile?.profile_preview ?? null) as Record<string, unknown> | null
   const name = asText(p?.name)
  const positioningLabel = asText(p?.positioning_label)
  const coreValue = asText(p?.core_value_proposition)
  const traits = asStringArray(p?.persona_traits)

  return (
    <aside className="tc-vip-aside">
      <div className="tc-data-card tc-vip-card" data-testid="vip-char-sidebar-profile">
        <div className="tc-vip-card-title">{t('step5.positioningSummary')}</div>
   {name ? (
          <div className="tc-vip-char-profile-summary">
            <QuickLine label={t('fields.ipName')} value={name} />
            {positioningLabel && <QuickLine label={t('flow.steps.3.label')} value={positioningLabel} />}
            {coreValue && <QuickLine label={t('fields.corePositioning')} value={coreValue} />}
            {traits.length > 0 && (
              <div>
                <span className="tc-vip-char-quick-line-label">{t('fields.coreTraits')}</span>
                <div className="tc-vip-step4-chip-row" style={{ marginTop: 4 }}>
   {traits.map((t, idx) => (
                    <Tag key={idx} className="tc-vip-analysis-tag">
                      {t}
                    </Tag>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
   <p className="tc-vip-card-hint">{t('characterWorkbench.noPositioningData')}</p>
        )}
        <Button type="link" size="small" className="tc-vip-char-sidebar-link" onClick={onViewFullProfile}>
          {t('characterWorkbench.viewFullPositioningProfile')}
        </Button>
      </div>
      <div className="tc-data-card tc-vip-card">
        <div className="tc-vip-card-title">{t('characterWorkbench.refineTipsTitle')}</div>
        <ul className="tc-vip-tips-list">
   <li>{t('characterWorkbench.refineTipsList.0')}</li>
          <li>{t('characterWorkbench.refineTipsList.1')}</li>
          <li>{t('characterWorkbench.refineTipsList.2')}</li>
        </ul>
      </div>

      <div className="tc-data-card tc-vip-card tc-vip-help-block">
        <div className="tc-vip-card-title">{t('step5.helpTitle')}</div>
        <Tooltip title={t('characterWorkbench.helpTooltip')}>
   <span className="tc-vip-char-help-link">{t('characterWorkbench.helpLink')}</span>
        </Tooltip>
      </div>
    </aside>
  )
}
